import { PlusIcon, XIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import { ReactNode } from 'react';

import clsxm from '@/lib/clsxm';

interface OptionCardProps {
  label: string;
  selected: boolean;
  onClick: () => void;
  icon: ReactNode;
  previewUrl?: string;
  emoji?: string;
  onClear?: () => void;
  showAddBadge?: boolean;
}

export default function OptionCard({
  label,
  selected,
  onClick,
  icon,
  previewUrl,
  emoji,
  onClear,
  showAddBadge,
}: OptionCardProps) {
  const showImage = selected && previewUrl;
  const showClear = selected && !!onClear;

  return (
    <div className='relative min-w-[88px]'>
      <button
        type='button'
        onClick={onClick}
        title={label}
        className={clsxm(
          'relative flex aspect-square w-full flex-col overflow-hidden rounded-2xl border-2 transition-colors',
          selected
            ? 'border-gray-300 bg-white dark:border-slate-600 dark:bg-slate-800'
            : 'border-dashed border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800/60 dark:hover:border-slate-500 dark:hover:bg-slate-800',
        )}
      >
        {showImage ? (
          <>
            <Image
              src={previewUrl}
              alt={label}
              fill
              sizes='(min-width: 640px) 25vw, 50vw'
              className='object-cover'
            />
            <div className='absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent px-2 pb-2 pt-6'>
              <p className='line-clamp-2 text-center text-xs font-semibold text-white'>
                {label}
              </p>
            </div>
          </>
        ) : (
          <div className='flex h-full w-full flex-col items-center justify-center gap-2 px-2 py-3'>
            {selected && emoji ? (
              <span className='text-4xl leading-none'>{emoji}</span>
            ) : (
              <span
                className={clsxm(
                  '[&>svg]:h-7 [&>svg]:w-7',
                  selected
                    ? 'text-primary-600 dark:text-primary-300'
                    : 'text-gray-500 dark:text-slate-400',
                )}
              >
                {icon}
              </span>
            )}
            <p
              className={clsxm(
                'line-clamp-2 text-center text-xs font-medium leading-tight',
                selected
                  ? 'text-primary-700 dark:text-primary-200'
                  : 'text-gray-700 dark:text-slate-300',
              )}
            >
              {label}
            </p>
          </div>
        )}
      </button>
      {showAddBadge && !selected && (
        <span className='pointer-events-none absolute end-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300'>
          <PlusIcon className='h-3 w-3' />
        </span>
      )}
      {showClear && (
        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          aria-label={`Clear ${label}`}
          className='absolute end-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80'
        >
          <XIcon className='h-3.5 w-3.5' />
        </button>
      )}
    </div>
  );
}
